import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { AlertTriangle, XCircle, AlertCircle } from 'lucide-react';

interface Alert {
  id: string;
  region: string;
  alert_type: 'error' | 'low_sources' | 'no_sources';
  error_message?: string;
  sources_found?: number;
  metadata?: {
    warnings?: string[];
    rejectedDomains?: string[];
  };
  email_sent: boolean;
  created_at: string;
}

interface AlertsTableProps {
  alerts: Alert[];
}

export function AlertsTable({ alerts }: AlertsTableProps) {
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'no_sources':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'low_sources':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'error':
        return 'Feil';
      case 'no_sources':
        return 'Ingen kilder';
      case 'low_sources':
        return 'For få kilder';
      default:
        return type;
    }
  };

  if (alerts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Siste varsler</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">Ingen varsler registrert</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Siste varsler</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Detaljer</TableHead>
              <TableHead>Kilder funnet</TableHead>
              <TableHead>E-post sendt</TableHead>
              <TableHead>Tidspunkt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {alerts.map((alert) => (
              <TableRow key={alert.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getAlertIcon(alert.alert_type)}
                    <span>{getAlertTypeLabel(alert.alert_type)}</span>
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {alert.region.charAt(0).toUpperCase() + alert.region.slice(1).replace('_', ' ')}
                </TableCell>
                <TableCell className="max-w-md">
                  {alert.error_message && (
                    <div className="text-sm text-muted-foreground truncate">
                      {alert.error_message}
                    </div>
                  )}
                  {alert.metadata?.warnings && alert.metadata.warnings.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {alert.metadata.warnings[0]}
                    </div>
                  )}
                  {alert.metadata?.rejectedDomains && alert.metadata.rejectedDomains.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {alert.metadata.rejectedDomains.length} domener avvist
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {alert.sources_found !== undefined ? (
                    <Badge variant={alert.sources_found === 0 ? 'destructive' : 'secondary'}>
                      {alert.sources_found}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={alert.email_sent ? 'default' : 'outline'}>
                    {alert.email_sent ? 'Ja' : 'Nei'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(new Date(alert.created_at), 'dd.MM.yyyy HH:mm', { locale: nb })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
